import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Check, RefreshCw, Pencil, Save, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, PLATFORM_META } from "@/lib/api";
import { toast } from "sonner";

export default function Result() {
    const { id } = useParams();
    const nav = useNavigate();
    const [job, setJob] = useState(null);
    const [copied, setCopied] = useState(null);
    const [regenerating, setRegenerating] = useState(null);
    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState("");
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`/repurpose/${id}`).then(({ data }) => {
            setJob(data);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [id]);

    const copy = async (platform, text) => {
        await navigator.clipboard.writeText(text);
        setCopied(platform);
        toast.success(`${PLATFORM_META[platform]?.label || platform} copied`);
        setTimeout(() => setCopied(null), 2000);
    };

    const regenerate = async (platform) => {
        setRegenerating(platform);
        try {
            const { data } = await api.post(`/repurpose/${id}/regenerate`, { platform });
            setJob(data);
            toast.success(`${PLATFORM_META[platform]?.label || platform} regenerated`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Regenerate failed");
        } finally {
            setRegenerating(null);
        }
    };

    const startEdit = (platform, content) => {
        setEditing(platform);
        setDraft(content);
    };

    const cancelEdit = () => {
        setEditing(null);
        setDraft("");
    };

    const saveEdit = async (platform) => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/repurpose/${id}/output`, { platform, content: draft });
            setJob(data);
            setEditing(null);
            setDraft("");
            toast.success("Saved");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    const deleteJob = async () => {
        if (!window.confirm("Delete this repurpose? This cannot be undone.")) return;
        try {
            await api.delete(`/repurpose/${id}`);
            toast.success("Deleted");
            nav("/app/history");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    if (loading) return <div className="p-10 font-mono-ui text-xs uppercase tracking-widest text-zinc-500">Loading…</div>;
    if (!job) return <div className="p-10 text-zinc-500">Not found.</div>;

    return (
        <div className="p-6 sm:p-10 max-w-6xl mx-auto" data-testid="result-root">
            <Link to="/app/history" data-testid="result-back" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-6">
                <ArrowLeft className="w-4 h-4" /> Back to history
            </Link>

            <div className="flex items-start justify-between gap-4">
                <div>
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-2">
                        // {new Date(job.created_at).toLocaleString()} — {job.source_type}
                    </div>
                    <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
                        {job.source_title || (job.source_type === "youtube" ? "YouTube video" : "Your content")}
                    </h1>
                </div>
                <Button
                    data-testid="result-delete-btn"
                    onClick={deleteJob}
                    variant="outline"
                    size="sm"
                    className="bg-transparent border-white/15 hover:bg-red-500/10 hover:border-red-500/40 text-zinc-400 hover:text-red-400 shrink-0"
                >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete
                </Button>
            </div>
            {job.source_type === "text" && (
                <p className="mt-3 text-zinc-500 text-sm line-clamp-3 max-w-3xl">{job.source}</p>
            )}

            <div className="mt-10 grid md:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {job.outputs.map((o) => (
                    <div key={o.platform} className="bg-[#0a0a0a] p-6 flex flex-col">
                        <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                            <div>
                                <div className="font-mono-ui text-[10px] tracking-[0.25em] uppercase text-zinc-500">// OUTPUT</div>
                                <div className="font-display font-bold text-xl mt-1">{PLATFORM_META[o.platform]?.label || o.platform}</div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {editing === o.platform ? (
                                    <>
                                        <Button data-testid={`save-${o.platform}`} onClick={() => saveEdit(o.platform)} disabled={saving} size="sm" className="bg-white text-black hover:bg-zinc-200">
                                            <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
                                        </Button>
                                        <Button data-testid={`cancel-${o.platform}`} onClick={cancelEdit} size="sm" variant="outline" className="bg-transparent border-white/15 hover:bg-white/5 text-white">
                                            <X className="w-3.5 h-3.5 mr-1.5" /> Cancel
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <Button data-testid={`edit-${o.platform}`} onClick={() => startEdit(o.platform, o.content)} variant="outline" size="sm" className="bg-transparent border-white/15 hover:bg-white/10 text-white">
                                            <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                                        </Button>
                                        <Button data-testid={`regen-${o.platform}`} onClick={() => regenerate(o.platform)} disabled={regenerating === o.platform} variant="outline" size="sm" className="bg-transparent border-white/15 hover:bg-white/10 text-white">
                                            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${regenerating === o.platform ? "animate-spin" : ""}`} />
                                            {regenerating === o.platform ? "…" : "Regenerate"}
                                        </Button>
                                        <Button data-testid={`copy-${o.platform}`} onClick={() => copy(o.platform, o.content)} variant="outline" size="sm" className="bg-transparent border-white/15 hover:bg-white text-white hover:text-black">
                                            {copied === o.platform ? <Check className="w-3.5 h-3.5 mr-1.5" /> : <Copy className="w-3.5 h-3.5 mr-1.5" />}
                                            {copied === o.platform ? "Copied" : "Copy"}
                                        </Button>
                                    </>
                                )}
                            </div>
                        </div>
                        {editing === o.platform ? (
                            <Textarea
                                data-testid={`edit-textarea-${o.platform}`}
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                rows={12}
                                className="bg-[#050505] border-white/20 focus:border-white text-white font-mono-ui text-sm resize-none flex-1"
                            />
                        ) : (
                            <pre className="font-mono-ui text-sm text-zinc-300 leading-relaxed whitespace-pre-wrap break-words flex-1">
                                {o.content}
                            </pre>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
